<script setup lang="ts">
import { MeasureResult } from '@Shared/utils/types';
import { useFrontlineFiltersStore } from '@Portal/stores/frontlineFilters';

interface FilterDropdownProps {
  modelValue: string;
  measureResults: Record<string, MeasureResult[]>;
}

const props = defineProps<FilterDropdownProps>();
const frontlineStore = useFrontlineFiltersStore();
const { measureResults, modelValue } = toRefs(props);

const emit = defineEmits(['update:modelValue']);

const dropdown = ref<boolean>(false);

const closeAndResetDropdown = () => {
  dropdown.value = false;
};

/*
 * Auto close on blur logic
 */

const programFilter = ref<HTMLElement | null>(null);

// detect when the user has clicked somewhere else.
const handleGlobalClick = (e: MouseEvent) => {
  if (programFilter.value && !programFilter.value.contains(e.target as HTMLElement)) {
    closeAndResetDropdown();
  }
};

onMounted(() => {
  document.addEventListener('click', handleGlobalClick);
});

onUnmounted(() => {
  document.removeEventListener('click', handleGlobalClick);
});

// when we select a value, we want to reset the state of the component.
const handleProgramSelect = (value: string) => {
  if (!modelValue.value) return;
  emit('update:modelValue', value);
  frontlineStore.selectProgramFilter(value);
  closeAndResetDropdown();
};

/*
 * Styling consts.
 */
const list = ['list-none p-0 m-0 py-[12px]'];
const listItem = ['py-[4px] px-4 hover:bg-[#CFD6E2] cursor-pointer'];
</script>

<template>
  <div
    ref="programFilter"
    :class="[
      'flex items-center gap-x-2 justify-between',
      'w-full relative box-border h-40px sm:h-30px bg-[#F4F6FB] border-rounded-1 p-8px cursor-pointer',
      'text-[1rem] sm:text-[0.75rem] font-[Roboto] font-semibold tracking-[0.02em] z-[50]',
    ]"
    @click="() => (dropdown ? closeAndResetDropdown() : (dropdown = true))"
  >
    <span
      :class="{
        'sm:text-[0.65rem]': false,
      }"
      >{{ modelValue }}</span
    >

    <div
      :class="[
        'transition',
        'i-mdi-chevron-left',
        'text-lg',
        {
          'rotate-90': dropdown,
          'rotate-270': !dropdown,
        },
      ]"
    ></div>

    <div
      id="program-filter-dropdown-window"
      tabindex="0"
      :class="[
        'absolute top-32px mobile:top-[90%] right-0',
        'cursor-default bg-[#F4F6FB] w-full box-border border-rounded-1 font-normal',
        'overflow-hidden transition',
        {
          // max height to animate the dropdown window
          'max-h-0px': !dropdown,
          'max-h-100vh': dropdown,
        },
      ]"
      @click.stop
    >
      <ul :class="[list, 'flex-grow-1']">
        <li @click="() => handleProgramSelect(key)" v-for="(_, key) in measureResults" :key="key" :class="listItem">
          {{ key }}
        </li>
      </ul>
    </div>
  </div>
</template>
